import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { RenderTagOption } from '../../../components/TwTagsField/TwTagsField';
import TwLoadCommodities from '../../../components/TwLoadCommodities/TwLoadCommodities';
import { Typography } from '@mui/material';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import TwTooltip from '../../../components/TwTooltip/TwTooltip';
import InfoIcon from '@mui/icons-material/Info';
import useAuth from '../../../providers/Auth/Auth.provider';
import { EntityType } from '../../../redux/Global.model';
import {
  useLazyPrefillOrderLTLStopsByIdQuery,
  useSearchOrderInTripMutation
} from '../../../redux/api/orders/ordersApi';
import debounce from 'lodash.debounce';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { OrderCommodities, PrefillOrderDataResponse } from '../../../redux/api/orders/orders.model';
import { nanoid } from '@reduxjs/toolkit';
import DateUtils from '../../../utils/dateUtils';
import StringUtils from '../../../utils/stringUtils';
import {
  StopStatus,
  Trip,
  TRIP_COVERAGE_STATUS,
  TripCommodities,
  TripStop
} from '../../../redux/api/trips/trips.model';
import { useDispatch } from 'react-redux';
import {
  addOrderCommoditiesToTrip,
  addOrderStopsToTrip,
  removeOrderCommoditiesFromTrip,
  removeOrderStopsFromTrip
} from '../../../redux/features/trip/tripSlice';
import { SliceStop } from '../../../redux/features/orders/ordersSlice';
import { useAppSelector } from '../../../redux/hooks';

interface TripOrderInfoProps {
  className?: string;
  ordersToConsolidate?: { id: string; readable_id: number | string }[];
}

export const formatStopsAndCommodities = (
  stops: TripStop[],
  commodities: OrderCommodities[],
  orderData?: {
    customerName: string;
    id: string;
    readable_id: string | number;
    customer_number: string;
  }
): { stops: SliceStop[]; commodities: TripCommodities[] } => {
  const sliceStops: SliceStop[] = stops
    .filter((stop) => stop.coverage === TRIP_COVERAGE_STATUS.UNCOVERED)
    .map((stop) => {
      return {
        id: stop.id,
        segment_id: '',
        ui_id: stop.id || nanoid(),
        order: 0,
        stop_identifier: stop.stop_identifier || '',
        provider_stop_identifier: stop.stop_identifier || '',
        provider_edi_stop_identifier: stop.edi_stop_identifier || '',
        provider_date:
          (stop.start_date && DateUtils.getDateInLocale(new Date(stop.start_date))) || null,
        provider_location: stop.location
          ? ({
              id: stop.location.id,
              label: stop.location.name,
              value: stop.location
            } as TwAutocompleteOption)
          : null,
        location: stop.location,
        provider_stop_type: stop.stop_type
          ? ({
              id: stop.stop_type,
              label: StringUtils.getLoadIntermediateStopLabel(stop.stop_type),
              value: stop.stop_type
            } as TwAutocompleteOption)
          : null,
        provider_time_from: stop.start_time
          ? DateUtils.convertTimeToDate(stop.start_time)
          : undefined,
        provider_time_to: stop.end_time ? DateUtils.convertTimeToDate(stop.end_time) : undefined,
        provider_include_location_notes: !!stop.notes || !!stop.contact,
        notes: stop.notes || '',
        contact: stop.contact,
        provider_stop_status: stop.status || StopStatus.notStarted,
        provider_check_in_date: stop.in_time ? new Date(stop.in_time) : null,
        provider_check_in_time: stop.in_time ? new Date(stop.in_time) : null,
        provider_stop_in_time_overriden: stop.is_in_time_overriden,
        provider_check_out_date: stop.out_time ? new Date(stop.out_time) : null,
        provider_check_out_time: stop.out_time ? new Date(stop.out_time) : null,
        provider_stop_out_time_overriden: stop.is_out_time_overriden,
        is_shared_stop: false,
        order_data: {
          order_id: orderData?.id || '',
          order_readable_id: orderData?.readable_id || ''
        } // adding the order data to stop
      };
    });

  const tripCommodities: TripCommodities[] = commodities.map((commodity) => {
    const comm: TripCommodities = {
      ...commodity,
      ui_uid: commodity.id || nanoid(),
      customer: orderData?.customerName || '',
      order_id: orderData?.id || '',
      customer_number: orderData?.customer_number || '',
      order_readable_id: orderData?.readable_id || ''
    };
    return comm;
  });

  return { stops: sliceStops, commodities: tripCommodities };
};

const TripOrderInfo = forwardRef<HTMLDivElement, TripOrderInfoProps>(
  ({ className, ordersToConsolidate }, ref) => {
    const {
      data: { userData }
    } = useAuth();
    const dispatch = useDispatch();
    const { addNotification } = useNotifications();
    const ordersInTrip = useAppSelector((state) => state.trip.ordersInTrip);
    const isEditTrip = useAppSelector((state) => state.navigation.isEditTripOpen);

    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const [searchOrderInTrip] = useSearchOrderInTripMutation({});
    const [prefillOrderStops] = useLazyPrefillOrderLTLStopsByIdQuery();
    const [selectedOrders, setSelectedOrders] = useState<TwAutocompleteOption[]>([]);
    const [ordersToSuggest, setOrdersToSuggest] = useState<TwAutocompleteOption[]>([]);

    const commoditiesEnabled =
      (userData?.tenantSettings && userData.tenantSettings['load-commodities']) || false;

    const debounceOrderSearch = useRef(
      debounce((text) => {
        searchOrderInTrip({ searchText: text })
          .unwrap()
          .then((result) => {
            const formatOrdersResult = result.map((order) => {
              const subLabels = [order.primary_client_name];
              if (order.customer_order_number) {
                subLabels.push(`Cust. # ${order.customer_order_number}`);
              }
              const subLabelText = subLabels.join(' | ');

              return {
                id: order.id,
                label: `#${order.readable_id}`,
                value: order.id,
                subLabel: subLabelText
              };
            });
            setOrdersToSuggest(formatOrdersResult);
          });
      }, 300)
    ).current;

    const handleSelectOrders = (e: any, value: any): void => {
      if (value) {
        // find added orders
        const ordersAdded = value.filter(
          (order: TwAutocompleteOption) => !selectedOrders.some((o) => o.id === order.id)
        );
        // find removed orders
        const removedOrders = selectedOrders.filter(
          (order) => !value.some((o: TwAutocompleteOption) => o.id === order.id)
        );

        if (ordersAdded.length > 0) {
          prefillOrderStops(ordersAdded[0].id)
            .unwrap()
            .then((result: PrefillOrderDataResponse) => {
              const orderData = {
                id: result.id,
                readable_id: result.readable_id,
                customer_number: result.customer_order_number || '',
                customerName: result.raw_customer?.name || ''
              };
              const { stops, commodities } = formatStopsAndCommodities(
                result.stops,
                result.order_commodities,
                orderData
              );

              // Add stops and commodities to the trip slice
              dispatch(addOrderStopsToTrip(stops));
              dispatch(addOrderCommoditiesToTrip(commodities));
            })
            .catch((error) => {
              console.error(error);
              addNotification({
                variant: 'error',
                message: 'Error while getting data from Order, please try again'
              });
            });
        }

        if (removedOrders.length > 0) {
          // remove stops and commodities from the trip
          dispatch(removeOrderStopsFromTrip(removedOrders[0].id));
          dispatch(removeOrderCommoditiesFromTrip(removedOrders[0].id));
        }

        setSelectedOrders(value);
      }
    };

    useEffect(() => {
      debounceOrderSearch('');
      return (): void => {
        debounceOrderSearch.cancel();
      };
    }, [debounceOrderSearch]);

    useEffect(() => {
      if (
        (isEditTrip && ordersInTrip.length > 0) ||
        (ordersToConsolidate && ordersToConsolidate.length > 0)
      ) {
        let ordersToUse = [] as { id: string; readable_id: string | number }[];

        if (isEditTrip) {
          ordersToUse = ordersInTrip;
        } else if (ordersToConsolidate) {
          ordersToUse = ordersToConsolidate;
        }

        const initialSelectedOrders = ordersToUse.map((order) => {
          return {
            id: order.id,
            label: `#${order.readable_id}`,
            value: order.id
          };
        });

        setSelectedOrders(initialSelectedOrders);
      }
    }, [isEditTrip, ordersInTrip, ordersToConsolidate]);

    return (
      <Box
        ref={ref}
        component="div"
        className={`${matchesMobileOrTablet ? 'route-info__main-import' : 'route-info__main'} ${
          className || ''
        }`}
      >
        <span className={matchesMobileOrTablet ? 'route-info__title-left' : 'route-info__title'}>
          Order Info
        </span>
        <span
          className={matchesMobileOrTablet ? 'route-info__sub-title-left' : 'route-info__sub-title'}
        >
          Add your order information
        </span>

        <Box sx={{ width: '100%' }}>
          <Typography
            variant="subtitle2"
            color="#7e858e"
            fontSize="14px"
            sx={{ margin: '5px 0', display: 'flex' }}
          >
            Orders
            <TwTooltip title="Add or remove orders for this trip">
              <InfoIcon sx={{ width: '1rem', height: '1rem', color: '#D1D5DB' }} />
            </TwTooltip>
          </Typography>
          <TwAutocomplete
            name="orders-selector"
            multiple
            optionList={ordersToSuggest}
            value={selectedOrders}
            onChange={handleSelectOrders}
            renderOption={(props, option): React.ReactNode => {
              return <RenderOption key={option.id} props={props} option={option} />;
            }}
            onInputChange={(e, value): void => {
              debounceOrderSearch(value);
            }}
            disableClearable
            sx={{
              '.MuiAutocomplete-popupIndicator': {
                display: 'none'
              }
            }}
            filterOptions={(options: any): any => options}
          />
          {commoditiesEnabled && (
            <>
              <Typography
                variant="subtitle2"
                fontWeight="600"
                color="#1F2937"
                fontSize="14px"
                textAlign={'left'}
                sx={{ margin: '20px 0' }}
              >
                Commodities
              </Typography>
              <TwLoadCommodities isImport={false} title="Commodities" entity={EntityType.TRIP} />
            </>
          )}
        </Box>
      </Box>
    );
  }
);

TripOrderInfo.displayName = 'TripOrderInfo';

export default TripOrderInfo;
